/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useServiceContext } from '../../root'
import Modal from '../Modal'
import SaveAndShare from './SaveAndShare'

interface SaveAndShareReportModalProps {
  type: string
  filterAssembler?: () => any
  metaAssembler?: () => any
  onClose: () => void
  isEdit?: boolean
  item?: any
  persistanceKey?: string
  isHideScheduleSettings?: boolean
}

const SaveAndShareReportModal: React.FC<SaveAndShareReportModalProps> = ({
  type,
  filterAssembler,
  metaAssembler,
  onClose,
  isEdit = false,
  item,
  persistanceKey,
  isHideScheduleSettings = false
}) => {
  const context = useServiceContext()
  const [isOpen, setIsOpen] = useState(false)

  const isEditMode = isEdit && item && item.id

  useEffect(() => {
    const openModal = async () => {
      if (!isEditMode) {
        // Validate form before opening (like legacy)
        try {
          if (context.store?.formRef?.validateForm) {
            const errors = await context.store.formRef.validateForm()
            if (!errors || Object.keys(errors).length === 0) {
              setIsOpen(true)
            } else {
              const { setNestedObjectValues } = await import('formik')
              if (context.store.formRef.setTouched) {
                context.store.formRef.setTouched(setNestedObjectValues(errors, true))
              }
              context.service.toast.info('PLS_APPLY_FILTERS')
              onClose()
            }
          } else {
            // No form to validate - open directly
            setIsOpen(true)
          }
        } catch (e) {
          context.service.toast.error('Reports Form was not found! Please contact administrator')
          onClose()
        }
      } else {
        // Edit mode - open directly
        setIsOpen(true)
      }
    }

    openModal()
  }, [])

  const doClose = () => {
    setIsOpen(false)
    onClose()
  }

  const doSave = async (name: string, schedule: any, sharedWith: any) => {
    try {
      const filterData = filterAssembler ? filterAssembler() : {}

      if (isEditMode) {
        await context.api.formFilters.update(name, item.id, schedule, sharedWith)
        context.service.toast.success('Saved')
      } else {
        // Get meta from metaAssembler if provided, otherwise from persist settings
        let meta: any

        if (metaAssembler) {
          // Use provided metaAssembler function
          meta = metaAssembler()
        } else {
          // Fall back to persist settings
          const GENERIC_TABLE_PERSIST_KEY = 'genericTable'
          const TABLE_SORT_DIR = { asc: 'asc', desc: 'desc' }
          const settings = context.service.persist.getGlobal<{ sort?: string; sortDir?: string }>(
            persistanceKey || GENERIC_TABLE_PERSIST_KEY
          )
          meta =
            settings && Object.keys(settings).length > 0 && settings.sort
              ? { sortBy: settings.sort, sortDesc: settings.sortDir === TABLE_SORT_DIR.desc }
              : {}
        }

        // Set meta to null for certain filter types
        if (type === 'CUSTOM' || type === 'ACCESS_RIGHTS' || type === 'SEARCH') {
          meta = null
        }

        await context.api.formFilters.create(name, type, filterData, meta, schedule, sharedWith)
        context.service.toast.success('SAVED_FILTERS_SHARE_MSG_SUCCESS')
      }

      doClose()
    } catch (error: any) {
      context.service.toast.error(error?.message || 'An error occurred while saving')
      throw error
    }
  }

  return createPortal(
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={context.service.filter.translate(
        type === 'PROPERTIES_SEARCH'
          ? 'SAVED_FILTERS_SAVE_PROPERTIES_SEARCH_H_TITLE'
          : isEditMode
            ? 'SAVED_FILTERS_UPDATE_REPORT_H_TITLE'
            : 'SAVED_FILTERS_SAVE_REPORT_H_TITLE'
      )}
      size="md"
      className="save-and-share-modal"
    >
      <SaveAndShare
        onSubmit={doSave}
        doClose={doClose}
        type={type}
        isEdit={isEditMode}
        item={item}
        isHideScheduleSettings={isHideScheduleSettings}
      />
    </Modal>,
    document.body
  )
}

export default SaveAndShareReportModal
